/**
 * The per-form templates a certificate is read against (piece 9; decision
 * 0347): one entry per state form the repository holds, keyed by the form
 * number the text detector already finds. A template names, for each fact
 * the modal wants, the fillable form's own field and the label the form
 * prints beside it, so the same entry serves a certificate filled in
 * Acrobat (its AcroForm fields) and one photographed or scanned (its words
 * and boxes, through the OcrProvider seam). 🔨 A form with no template
 * still yields its state and its raw field map; nothing here refuses.
 */
import { findLabel, linesOf, tickedBesideLabel, valueAtLabel, type OcrPage, type PixelSampler } from '../ocr/ocr';
import { detectExemptionReason, detectFormCode, detectStateCode, isMultistateForm } from './taxExemption';

export type FormFieldValue = string | boolean;
/** A fillable form's fields: text as typed, a box as true or its own value when the field holds several boxes, a radio as its chosen option. */
export type FormFields = Readonly<Record<string, FormFieldValue>>;

/** Where a fact stands: a field name on the fillable form, and the label the form prints beside it. */
export interface FormAnchor {
  /** One field, or several joined in order for a number the form splits across boxes. */
  field?: string | readonly string[];
  join?: string;
  /** The printed label, matched word for word; the value is what follows it on the line, or the line beneath. */
  label?: string;
  where?: 'right' | 'below';
}

export interface ReasonRule extends FormAnchor {
  code: string;
  /** For a field holding several boxes or a radio, the box's own value; absent, any ticked state counts. */
  value?: string;
  /** A ticked box whose reason is the text the form asks for beside it; that text is read for a cue. */
  fromText?: string;
}

export interface BlanketRule extends FormAnchor {
  value?: string;
  blanket: boolean;
}

/** A box the fill ticks: the field, and the value its own widget carries where one field holds several boxes. */
export interface FillTick {
  field: string;
  value?: string;
}

/**
 * Where the fill writes a fact the form gives no field: the page's index and the text's baseline, in points from the
 * page's lower left corner, and the type size.
 */
export interface FillPlace {
  page: number;
  x: number;
  y: number;
  size: number;
}

/** One reason the organization buys exempt for, as the form claims it: the box ticked, the field its number goes in, the words a line asks for. */
export interface FillReason {
  tick: FillTick;
  numberField?: string;
  text?: { field: string; value: string };
}

/** The reasons a one-time form claims (decision 9600), from the multistate set. */
export type OneTimeExemptionReason = 'Resale' | 'Manufacturing';

/**
 * 🔒 Decision 9600: how the application fills the form as the organization's own unit certificate, the purchaser's side
 * and the seller's. Each name is the fillable form's own field; a form with no such map is never filled.
 */
export interface CertificateFillMap {
  /** The single-purchase box. */
  unit: FillTick;
  /** The boxes every fill ticks, a form's sales and use tax line among them. */
  ticks: readonly FillTick[];
  sellerName: string;
  sellerStreet?: string;
  sellerCity?: string;
  sellerState?: string;
  sellerZip?: string;
  purchaserName: string;
  purchaserStreet?: string;
  purchaserCity?: string;
  purchaserState?: string;
  purchaserZip?: string;
  /** The date the form is signed, written as the form prints a date, month first. */
  date: string;
  reasons: Readonly<Partial<Record<OneTimeExemptionReason, FillReason>>>;
  /** The signature box, where the signer's name and title are written for the signer to sign beside. */
  signer: FillPlace;
  /** The purchase order the unit certificate covers, written where the form names no transaction. */
  transaction: FillPlace;
}

export interface CertificateFormTemplate {
  /** The form's own number, as `STATE_FORM_CODES` lists it, or a name for a form with none. */
  code: string;
  stateCode: string | null;
  /** Field names all present on the fillable form and on no other, so the form is known off its fields alone. */
  signature: readonly string[];
  /** Words the printed form carries, all of them, for a form read off its text or its picture. */
  cues: readonly string[];
  purchaserName: readonly FormAnchor[];
  identifier: readonly FormAnchor[];
  signedOn: readonly FormAnchor[];
  expiresOn: readonly FormAnchor[];
  /** The reason boxes in the order the form prints them; the first ticked one is the reason. */
  reasons: readonly ReasonRule[];
  /** Free text the form takes the reason in, read for a cue before the boxes. */
  reasonText: readonly FormAnchor[];
  /** The reason a form built for one purpose claims by its existence, when no box says otherwise. */
  defaultReason: string | null;
  blanket: readonly BlanketRule[];
  defaultBlanket: boolean | null;
  /** 🔒 Decision 9600: the purchaser's and the seller's fields a one-time form fills; absent, the form is never filled. */
  fill?: CertificateFillMap;
}

export interface CertificateFacts {
  formCode: string | null;
  stateCode: string | null;
  purchaserName: string | null;
  identifier: string | null;
  isBlanket: boolean | null;
  reasonCode: string | null;
  /** ISO dates. */
  signedOn: string | null;
  expiresOn: string | null;
}

export const EMPTY_CERTIFICATE_FACTS: CertificateFacts = { formCode: null, stateCode: null, purchaserName: null, identifier: null, isBlanket: null, reasonCode: null, signedOn: null, expiresOn: null };

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

/** A date as a form takes it — 3/14/2026, 03-14-26, 2026-03-14, March 14, 2026, 14 March 2026 — as an ISO date, or null. */
export function parseFormDate(text: string | null | undefined): string | null {
  const value = (text ?? '').trim();
  if (!value) return null;
  const iso = /(\d{4})-(\d{1,2})-(\d{1,2})/.exec(value);
  if (iso) return isoDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const us = /(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/.exec(value);
  if (us) return isoDate(fullYear(Number(us[3]), us[3]!.length), Number(us[1]), Number(us[2]));
  const long = /([A-Za-z]+)\.?\s+(\d{1,2}),?\s+(\d{4})/.exec(value) ?? /(\d{1,2})\s+([A-Za-z]+)\.?,?\s+(\d{4})/.exec(value);
  if (long) {
    const monthWord = /^\d/.test(long[1]!) ? long[2]! : long[1]!;
    const day = /^\d/.test(long[1]!) ? long[1]! : long[2]!;
    const month = MONTHS.findIndex((m) => m.startsWith(monthWord.toLowerCase().slice(0, 3)));
    if (month >= 0) return isoDate(Number(long[3]), month + 1, Number(day));
  }
  return null;
}

function fullYear(year: number, digits: number): number {
  return digits <= 2 ? 2000 + year : year;
}

function isoDate(year: number, month: number, day: number): string | null {
  if (year < 1900 || year > 2200 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const SST_STATES = ['AR', 'GA', 'IA', 'IN', 'KS', 'KY', 'MI', 'MN', 'NC', 'ND', 'NE', 'NJ', 'NV', 'OH', 'OK', 'RI', 'SD', 'TN', 'UT', 'VT', 'WA', 'WI', 'WV', 'WY'];
const MTC_STATES = ['AKARSSTC 1', 'MO 19', 'AL 2', 'NE', 'AR', 'NV 20', 'AZ 3', 'NJ', 'CA 4', 'NM 521', 'CO 56', 'NC 22', 'CT 7', 'ND', 'FL8', 'OH23', 'GA9', 'OK 24', 'HI 510', 'PA 25', 'ID11', 'RI 26', 'IL 512', 'SC', 'IA', 'SD 27', 'KS13', 'TN 28', 'KY14', 'TX 29', 'ME 15', 'UT', 'MD 16', 'VT 30', 'MI 17', 'WA 31', 'MN 18', 'WI 32'];

const f = (field: string | readonly string[], label?: string, where?: 'right' | 'below'): FormAnchor => ({ field, ...(label ? { label } : {}), ...(where ? { where } : {}) });
const l = (label: string, where?: 'right' | 'below'): FormAnchor => ({ label, ...(where ? { where } : {}) });
const r = (code: string, field?: string, label?: string, value?: string): ReasonRule => ({ code, ...(field ? { field } : {}), ...(label ? { label } : {}), ...(value !== undefined ? { value } : {}) });
const b = (blanket: boolean, field?: string, label?: string, value?: string): BlanketRule => ({ blanket, ...(field ? { field } : {}), ...(label ? { label } : {}), ...(value !== undefined ? { value } : {}) });

/** The templates, one per committed form; a state with two revisions in the folder carries both. */
export const CERTIFICATE_FORM_TEMPLATES: readonly CertificateFormTemplate[] = [
  {
    code: 'Form 5000', stateCode: 'AZ', signature: ['purchaser_name', 'purchaser_tax_id', 'chkbx01'], cues: ['5000A'],
    purchaserName: [f('purchaser_name', 'Purchaser’s Name', 'below')], identifier: [f('purchaser_tax_id')], signedOn: [f('signed_date', 'Date', 'below')], expiresOn: [f('expire_date', 'Through')],
    reasons: [], reasonText: [], defaultReason: 'Resale',
    blanket: [b(false, 'chkbx01', 'Single Purchase Certificate'), b(true, 'chkbx02', 'Period:')], defaultBlanket: null,
  },
  {
    code: 'CDTFA-230', stateCode: 'CA', signature: ['nop', 'pnps', 'question1'], cues: ['CDTFA-230'],
    purchaserName: [f('nop', 'NAME OF PURCHASER', 'below')], identifier: [f('question1', 'seller’s permit number:')], signedOn: [f('DATE', 'DATE', 'below')], expiresOn: [],
    reasons: [], reasonText: [], defaultReason: 'Resale', blanket: [], defaultBlanket: null,
  },
  {
    code: 'G-17', stateCode: 'HI', signature: ['II7', 'II12', 'II15'], cues: ['G-17'],
    purchaserName: [f('II15', 'Name of Purchaser', 'below')], identifier: [f('II7', 'Hawaii Tax Identification No.')], signedOn: [f('II21', 'Date', 'below')], expiresOn: [],
    reasons: [r('Resale', 'II12', 'are purchases for resale at retail'), r('Resale', 'II13', 'are purchases for resale at wholesale')], reasonText: [], defaultReason: 'Resale',
    blanket: [], defaultBlanket: true,
  },
  {
    code: '31-014', stateCode: 'IA', signature: ['Purchaser Name', 'Blanket Certificate', 'Resale'], cues: ['31-014', 'Purchaser is doing business as'],
    purchaserName: [f('Purchaser Name', 'Purchaser Name')], identifier: [f('SalesUseExcise Tax Permit Number if required', 'Permit Number'), f('permit number required')], signedOn: [f('Date', 'Date')], expiresOn: [],
    reasons: [
      r('Resale', 'Resale', 'Resale'), r('Manufacturing', 'Processing', 'Processing'), r('Agricultural', 'Qualifying Farm Machinery/Equipment', 'Qualifying farm machinery/equipment'),
      r('Agricultural', 'Qualifying Farm Replacement Parts', 'Qualifying farm replacement parts'), r('Manufacturing', 'Qualifying Manufacturing Machinery/Equipment', 'Qualifying manufacturing machinery/equipment'),
      r('DirectPay', 'Direct Pay', 'Direct pay'), r('Other', 'Leasing', 'Leasing'), r('Other', 'Research and Development Equipment'), r('Other', 'Pollution Control Equipment'), r('Other', 'Recycling Equipment'),
      r('Other', 'Qualifying Computer'), r('Other', 'Qualifying Replacement Parts/Supplies'), r('Other', 'QualifyingComputerSoftWare'), r('Agricultural', 'Grain bins'), r('Other', 'Other 2', 'Other:'),
      r('StateLocalGovernment', 'Doing Business as - Government agency', 'Governmental agency'), r('Educational', 'Doing Business as - Private nonprofit education institution', 'Private nonprofit educational institution'),
      r('Charitable', 'Doing Business as - Nonprofit hospital', 'Nonprofit hospital'), r('Charitable', 'Doing Business as - Nonprofit museum', 'Nonprofit museum'), r('Charitable', 'Doing Business as - Nonprofit food bank', 'Nonprofit food bank'),
    ],
    reasonText: [f('Exemption for - Other')], defaultReason: null,
    blanket: [b(true, 'Blanket Certificate', 'Blanket Certificate:', 'Yes'), b(false, 'Single Purchase', 'Single Purchase:', 'Yes')], defaultBlanket: null,
  },
  {
    code: '31-014', stateCode: 'IA', signature: ['Purchaser Name', 'PurchaserBusiness'], cues: ['31-014'],
    purchaserName: [f('Purchaser Name', 'Purchaser Name')], identifier: [f('SalesUseExcise Tax Permit Number if required'), f('permit number required')], signedOn: [f('Date', 'Date')], expiresOn: [],
    reasons: [
      r('Resale', 'Resale', 'Resale'), r('Manufacturing', 'Processing', 'Processing'), r('Agricultural', 'Qualifying Farm Machinery/Equipment'), r('Agricultural', 'Qualifying Farm Replacement Parts'),
      r('Manufacturing', 'Qualifying Manufacturing Machinery/Equipment'), r('DirectPay', 'Direct Pay', 'Direct Pay'), r('Other', 'Leasing'), r('Other', 'Research and Development Equipment'), r('Other', 'Pollution Control Equipment'),
      r('Other', 'Recycling Equipment'), r('Other', 'Qualifying Computer'), r('Other', 'Qualifying Replacement Parts/Supplies'), r('Other', 'QualifyingComputerSoftWare'), r('Other', 'Other 2'),
      r('StateLocalGovernment', 'PurchaserBusiness', undefined, 'Governmental Agency'), r('Educational', 'PurchaserBusiness', undefined, 'NonProfitEducational'), r('Charitable', 'PurchaserBusiness', undefined, 'Nonprofit Hospital'),
      r('Charitable', 'PurchaserBusiness', undefined, 'NonprofitMuseum'), r('Agricultural', 'PurchaserBusiness', undefined, 'Farmer'),
    ],
    reasonText: [f('QualifyingOther')], defaultReason: null, blanket: [], defaultBlanket: null,
  },
  {
    code: 'ST-101', stateCode: 'ID', signature: ['BuyerName', 'L1a', 'L2Farming'], cues: ['ST-101'],
    purchaserName: [f('BuyerName', 'Buyer’s Name')], identifier: [f('LbIDRetailerPermitNo', 'seller’s permit number'), f('EIN_DL'), f('L5TribalNo')], signedOn: [f('DateSign', 'Date')], expiresOn: [],
    reasons: [
      r('Resale', 'LbIDRegRetailer', 'Idaho registered retailer'), r('Resale', 'L1bWholesaler', 'Wholesaler only'), r('Resale', 'L1bRetailerOnly'), r('Resale', 'L1bOutOfState', 'Out-of-state retailer'),
      r('Manufacturing', 'L2Manufacturing', 'Manufacturing'), r('Manufacturing', 'L2Processing', 'Processing'), r('Manufacturing', 'L2Fabric'), r('Manufacturing', 'L2ProdExemption'), r('Manufacturing', 'L2Mining', 'Mining'),
      r('Manufacturing', 'L2Logging', 'Logging'), r('Manufacturing', 'L2Publish'), r('Manufacturing', 'L2Broadcasting', 'Broadcasting'), r('Agricultural', 'L2Farming', 'Farming'), r('Agricultural', 'L2Ranch', 'Ranching'),
      r('Agricultural', 'L4dAGIrrigation'), r('Agricultural', 'L5Irrigation'), r('Agricultural', 'L5Livestock'), r('TribalGovernment', 'L3AmericanIndianTribes', 'American Indian tribes'), r('TribalGovernment', 'L5AmIndian'),
      r('StateLocalGovernment', 'L3Govern', 'Government'), r('Educational', 'L3Schools', 'Schools'), r('Charitable', 'L3Hospitals', 'Hospitals'), r('Charitable', 'L3Foodbank'), r('Charitable', 'L3Museums'), r('Charitable', 'L3AmRedCross'),
      r('Charitable', 'L3VolunteerFire'), r('Charitable', 'L3Canal'), r('Charitable', 'L3CreditUnions'), r('Charitable', 'L3EMS'), r('Charitable', 'L3Forest'), r('Charitable', 'L3IndependentLiving'), r('Charitable', 'L3Dental'),
      r('Charitable', 'L3Advo'), r('Charitable', 'L3BlindServices'), r('Charitable', 'L3SrCitizen'), r('Charitable', 'L3Qualifying'), r('Charitable', 'L3Amtrak'), r('Religious', 'L5ChurchFood'), r('Other', 'L5OtherGoods', 'Other'),
    ],
    reasonText: [], defaultReason: null, blanket: [], defaultBlanket: null,
  },
  {
    code: 'CRT-61', stateCode: 'IL', signature: ['Buyer Name', '11', '17'], cues: ['CRT-61'],
    purchaserName: [f('Buyer Name', 'Identify the purchaser', 'below')], identifier: [f(['Registration number', 'Registration number 2'], 'Account ID number'), f('Account/registration number')], signedOn: [f('Date', 'Date')], expiresOn: [],
    reasons: [], reasonText: [], defaultReason: 'Resale',
    blanket: [b(true, '17', 'I am the identified purchaser, and I certify that all of the', '1'), b(true, '17', undefined, '2')], defaultBlanket: null,
  },
  {
    code: 'ST-105', stateCode: 'IN', signature: ['Name of Purchaser', 'Type of Exemption', 'Type of Request'], cues: ['ST-105'],
    purchaserName: [f('Name of Purchaser', 'Name of Purchaser', 'below')], identifier: [{ field: ['TID', 'LOC Number'], join: '-', label: 'TID#' }, f('State ID Number', 'State ID#')], signedOn: [f('Date', 'Date')], expiresOn: [],
    reasons: [
      r('Manufacturing', 'Type of Exemption', 'manufacturing', 'Sales of Manufacturing'), r('Charitable', 'Type of Exemption', 'nonprofit', 'Sales to Nonprofit'), r('Resale', 'Type of Exemption', 'resale', 'Sales of Personal Property'),
      r('Agricultural', 'Type of Exemption', 'farmers', 'Sales to Farmers'), r('Other', 'Type of Exemption', 'contractor', 'Sales to Contractor'), r('StateLocalGovernment', 'Type of Exemption', 'Indiana Governmental Unit', 'Sales to IN Gov'),
      r('FederalGovernment', 'Type of Exemption', 'United States Federal Government', 'Sales to Fed Gov'), r('Other', 'Type of Exemption', 'Other', 'Other Type'),
    ],
    reasonText: [f('Other')], defaultReason: null,
    blanket: [b(true, 'Type of Request', 'blanket purchase exemption request', 'Blanket Pruchase'), b(false, 'Type of Request', 'single purchase exemption request', 'Single Purchase')], defaultBlanket: null,
  },
  {
    code: 'ST-28A', stateCode: 'KS', signature: ['Purchaser', 'tax registration number', 'Date - ST-28A'], cues: ['ST-28A'],
    purchaserName: [f('Purchaser', 'Purchaser:')], identifier: [f('tax registration number', 'tax registration number')], signedOn: [f('Date - ST-28A', 'Date:')], expiresOn: [],
    reasons: [], reasonText: [], defaultReason: 'Resale', blanket: [], defaultBlanket: null,
  },
  {
    code: 'ST-28G', stateCode: 'KS', signature: ['purchaser - name of federal entity'], cues: ['ST-28G'],
    purchaserName: [f('purchaser - name of federal entity', 'Purchaser:')], identifier: [], signedOn: [l('Date:')], expiresOn: [],
    reasons: [], reasonText: [], defaultReason: 'FederalGovernment', blanket: [], defaultBlanket: null,
  },
  {
    code: 'Form 3372', stateCode: 'MI', signature: [], cues: ['3372', 'Michigan Sales and Use Tax Certificate of Exemption'],
    purchaserName: [l('Name', 'right')], identifier: [l('Enter Sales Tax License Number:'), l('account number:')], signedOn: [l('Date Signed', 'below')], expiresOn: [l('Expiration Date (maximum of four years):')],
    reasons: [
      r('Other', undefined, '1. For Lease.'), r('Resale', undefined, '2. For Resale at Retail.'), r('DirectPay', undefined, '3. Direct Pay'), r('Agricultural', undefined, '4. Agricultural Production.'),
      r('StateLocalGovernment', undefined, '5. Government Entity'), r('Other', undefined, '6. Contractor'), r('Resale', undefined, '7. For Resale at Wholesale.'), r('Manufacturing', undefined, '8. Industrial Processing.'),
      r('Charitable', undefined, '9. Nonprofit Internal Revenue Code'), r('Charitable', undefined, '10. Nonprofit Organization'), r('Other', undefined, '11. Rolling Stock'), r('Other', undefined, '12. Other (explain):'),
    ],
    reasonText: [], defaultReason: null,
    blanket: [b(false, undefined, 'A. One-Time Purchase'), b(true, undefined, 'B. Blanket Certificate.'), b(true, undefined, 'C. Blanket Certificate')], defaultBlanket: null,
  },
  {
    code: 'Form 149', stateCode: 'MO', signature: ['Name', 'check1', 'Purchasers Manufacturing Percentage'], cues: ['Form 149'],
    purchaserName: [f('Name', 'Name', 'below')], identifier: [f('Text2', 'Missouri Tax I.D. Number', 'below'), f('r  Purchases of Tangible Personal Property for resale Retailers State Tax ID Number', 'Retailer’s State Tax ID Number'), f('Retailers Missouri Tax ID Number', 'Retailer’s Missouri Tax I.D. Number')],
    signedOn: [f('Text4', 'Date (MM/DD/YYYY)', 'below')], expiresOn: [],
    reasons: [
      r('Resale', 'check1', 'Purchases of Tangible Personal Property for resale:'), r('Resale', 'check2', 'Purchases of Taxable Services for resale'), r('Resale', 'check3', 'Purchases by Manufacturer or Wholesaler for Wholesale:'),
      r('Resale', 'check4', 'Purchases by Motor Vehicle Dealer:'), r('Manufacturing', 'check5', 'Ingredient or Component Part'), r('Manufacturing', 'check6', 'Plant Expansion'), r('Manufacturing', 'check8', 'Manufacturing Machinery, Equipment, and Parts'),
      r('Manufacturing', 'check9'), r('Manufacturing', 'check7', 'Material Recovery Processing'), r('Manufacturing', 'check10', 'Manufacturing Chemicals and Materials'), r('Manufacturing', 'check11'), r('Manufacturing', 'check12'),
      r('Manufacturing', 'check13', 'Machinery and Equipment Used or Consumed in Manufacturing'), r('Manufacturing', 'check14', 'Research and Development'), r('Manufacturing', 'check19'), r('Agricultural', 'check15.0.0', 'Agricultural'),
      r('Other', 'check16', 'Common Carrier'), r('Other', 'check17', 'Locomotive Fuel'), r('Other', 'check18', 'Air and Water Pollution Control'), r('Other', 'Broadband'), r('Other', 'Check Box CMV', 'Commercial Motor Vehicles'),
      r('Other', 'NSE CHeck BOx', 'Nuclear Security Enterprise'), r('Other', 'check20', 'Other'),
    ],
    reasonText: [f('r Other')], defaultReason: null, blanket: [], defaultBlanket: null,
  },
  {
    code: 'Form 13', stateCode: 'NE', signature: ['NameLegal Name', 'blanket', 'exemption category'], cues: ['Nebraska Resale or Exempt Sale Certificate'],
    purchaserName: [f('Name_1', 'Name and Mailing Address of Purchaser', 'below'), f('NameLegal Name')], identifier: [f('Nebraska Sales Tax Permit Number'), f('Nebraska Certificate of Exemption State ID number'), f('nebraska sales or use tax permit number'), f('Foreign state sales tax number')],
    signedOn: [f('Date', 'Date')], expiresOn: [],
    reasons: [
      r('Resale', 'purchase for resale (complete section A)', 'Purchase for Resale'), r('StateLocalGovernment', 'exemption category', undefined, '1'), r('Other', 'exemption category', undefined, '2'), r('Charitable', 'exemption category', undefined, '3'),
      r('Other', 'exemption category', undefined, '4'), r('Manufacturing', 'exemption category', undefined, '5'), r('Other', 'exemption category', undefined, '6'), r('Other', 'contractor (complete section C)', 'Contractor'),
    ],
    reasonText: [], defaultReason: null,
    blanket: [b(true, 'blanket', 'Blanket'), b(false, 'single purchase', 'Single Purchase')], defaultBlanket: null,
  },
  {
    code: 'ST-3', stateCode: 'NJ', signature: ['New Jersey Taxpayer Identification Number', 'Group1'], cues: ['New Jersey', 'Resale Certificate'],
    purchaserName: [f('Name_2', 'As registered with the New Jersey Division of Taxation', 'below')], identifier: [f('New Jersey Taxpayer Identification Number', 'New Jersey Taxpayer Identification Number', 'below')], signedOn: [f('Date', 'Date')], expiresOn: [],
    reasons: [], reasonText: [], defaultReason: 'Resale',
    blanket: [b(false, 'Group1', 'Single-Purchase Certificate', 'Choice1'), b(true, 'Group1', 'Blanket Certificate', 'Choice2')], defaultBlanket: null,
  },
  {
    code: 'ST-120', stateCode: 'NY', signature: ['purchaser\'s name', 'single-use certificate'], cues: ['ST-120', 'Resale Certificate'],
    purchaserName: [f('purchaser\'s name', 'Name of purchaser', 'below')], identifier: [f('coa number 1', 'My valid Certificate of Authority number is'), f('registration number', 'registration number')], signedOn: [f('date signed', 'Date signed', 'below')], expiresOn: [],
    reasons: [], reasonText: [], defaultReason: 'Resale',
    blanket: [b(false, 'single-use certificate', 'Single-use certificate', 'Yes'), b(true, 'single-use certificate', 'Blanket certificate', 'No')], defaultBlanket: null,
  },
  {
    code: 'ST-120.1', stateCode: 'NY', signature: ['name of purchasing contractor', 'box a'], cues: ['ST-120.1'],
    purchaserName: [f('name of purchasing contractor', 'Name of purchasing contractor', 'below')], identifier: [f('enter your sales tax vendor id number', 'sales tax vendor identification number')], signedOn: [f('date prepared', 'Date prepared', 'below')], expiresOn: [],
    reasons: [], reasonText: [], defaultReason: 'Other', blanket: [], defaultBlanket: null,
  },
  {
    code: 'ST-121', stateCode: 'NY', signature: ['name of purchaser', 'check one', '(A)'], cues: ['ST-121'],
    purchaserName: [f('name of purchaser', 'Name of purchaser', 'below')], identifier: [], signedOn: [f('date', 'Date', 'below')], expiresOn: [],
    reasons: [
      r('Manufacturing', '(A)', 'A. Machinery and equipment, parts, tools, and'), r('Manufacturing', '(B)'), r('Manufacturing', '(C)'), r('Manufacturing', '(D)'), r('Manufacturing', '(E)'), r('Manufacturing', '(F)'), r('Manufacturing', '(G)'),
      r('Manufacturing', '(H)'), r('Manufacturing', '(I)'), r('Manufacturing', '(J)'), r('Other', '(K)'), r('Other', '(L)'), r('Other', '(M)'), r('Other', '(N)'), r('Other', '(O)'), r('Other', '(P)'), r('Other', '(Q)'), r('Other', '(R)'),
      r('Manufacturing', '(S)'), r('Manufacturing', '(T)'), r('Other', '(U)', 'U. Other'),
    ],
    reasonText: [f('(u) line')], defaultReason: null,
    blanket: [b(false, 'check one', 'Single-purchase certificate', 'Single'), b(true, 'check one', 'Blanket certificate', 'Blanket')], defaultBlanket: null,
  },
  {
    code: 'STEC B', stateCode: 'OH', signature: ['1', '2', '3', '4', '5', '6', '7', '8', '9'], cues: ['STEC B'],
    purchaserName: [f('4', 'Purchaser’s name')], identifier: [], signedOn: [f('9', 'Date signed')], expiresOn: [],
    reasons: [], reasonText: [f('2', 'Purchaser must state a valid reason for claiming exception or exemption.', 'below')], defaultReason: null, blanket: [], defaultBlanket: true,
  },
  {
    code: 'STEC B', stateCode: 'OH', signature: ['purchaser_name', 'voider', 'signers_title'], cues: ['Blanket Exemption Certificate', 'Ohio'],
    purchaserName: [f('purchaser_name', 'Purchaser’s name')], identifier: [f('purchaser_tax_id')], signedOn: [f('signed_date', 'Date signed')], expiresOn: [],
    reasons: [], reasonText: [f('equipment_description')], defaultReason: null, blanket: [], defaultBlanket: true,
  },
  {
    code: 'REV-1220', stateCode: 'PA', signature: ['Name of Purchaser or Lessee', 'Check one- PA'], cues: ['REV-1220'],
    purchaserName: [f('Name of Purchaser or Lessee', 'Name of Purchaser or Lessee')], identifier: [f('Exemption Number', 'Sales Tax Exemption Number'), f('License Number', 'License ID'), f('EIN (2)', 'EIN')], signedOn: [f('Date (2)', 'Date')], expiresOn: [],
    reasons: [
      r('Resale', '3. Check corresponding block below', '3. Property will be resold'), { code: 'Other', field: '2. Check corresponding block below', label: '2. Purchaser is a/an:', fromText: '2. Purchaser is a/on' },
      r('Other', '1. Check corresponding block below', '1. Property or services will be used directly'), r('Other', '4. Check corresponding block below', '4. Property or services will be used directly'),
      r('Other', '5. Check corresponding block below', '5. Exempt wrapping supplies'), r('Other', '6. Check corresponding block below', '6. Canned computer software'), r('Other', '7. Check corresponding block below', '7. Canned computer software licenses'),
      { code: 'Other', field: '8. Check corresponding block below', label: '8. Other', fromText: 'Other' },
    ],
    reasonText: [], defaultReason: null,
    blanket: [b(false, 'Check one- PA', 'PENNSYLVANIA TAX UNIT EXEMPTION CERTIFICATE', '6'), b(true, 'Check one- PA', 'PENNSYLVANIA TAX BLANKET EXEMPTION CERTIFICATE', '9')], defaultBlanket: null,
    // 🔒 Decision 9600: the unit certificate the organization fills for a vendor. Reason 3 takes the License ID; reason 1 names the operation and asks no number.
    fill: {
      unit: { field: 'Check one- PA', value: '6' },
      ticks: [{ field: 'State and Local Sales and Use Tax' }],
      sellerName: 'Name of Seller or Lessor', sellerStreet: 'Street', sellerCity: 'City', sellerState: 'State', sellerZip: 'Zip Code',
      purchaserName: 'Name of Purchaser or Lessee', purchaserStreet: 'Street (2)', purchaserCity: 'City (2)', purchaserState: 'State (2)', purchaserZip: 'Zip Code (2)',
      date: 'Date (2)',
      reasons: {
        Resale: { tick: { field: '3. Check corresponding block below' }, numberField: 'License Number' },
        Manufacturing: { tick: { field: '1. Check corresponding block below' }, text: { field: 'in performing purchaser\'s operation of:', value: 'Manufacturing' } },
      },
      signer: { page: 0, x: 274, y: 222, size: 5.5 },
      transaction: { page: 0, x: 20, y: 676, size: 8 },
    },
  },
  {
    code: '01-339', stateCode: 'TX', signature: ['Purchaser', 'Exemption purchaser', 'Exemption reason'], cues: ['01-339'],
    purchaserName: [f('Exemption purchaser'), f('Purchaser', 'Name of purchaser, firm or agency', 'below')], identifier: [f('Permit number', 'Texas Sales and Use Tax Permit Number', 'below'), f('RFCnumber')],
    signedOn: [f('Exempt purchaser sig date'), f('Purchaser sig date', 'Date', 'right')], expiresOn: [],
    reasons: [r('Resale', 'Permit number'), r('Resale', 'Purchaser')], reasonText: [f('Exemption reason', 'Purchaser claims this exemption for the following reason:', 'below')], defaultReason: null, blanket: [], defaultBlanket: null,
  },
  {
    code: 'TC-721G', stateCode: 'UT', signature: ['diplomat number', 'school name'], cues: ['TC-721G'],
    purchaserName: [f('businessname', 'Name of institution claiming exemption (purchaser)', 'below')], identifier: [f('licenseno 3', 'Sales Tax License No.'), f('licenseno 4'), f('diplomat number', 'Foreign diplomat number:')], signedOn: [f('date01', 'Date', 'below')], expiresOn: [],
    reasons: [
      r('FederalGovernment', '01', 'UNITED STATES GOVERNMENT OR NATIVE AMERICAN TRIBE'), r('Educational', '02', 'CONSTRUCTION MATERIALS PURCHASED FOR SCHOOLS'), r('ForeignDiplomat', '03', 'FOREIGN DIPLOMAT'),
      r('StateLocalGovernment', '04', 'UTAH LOCAL GOVERNMENTS AND PUBLIC ELEMENTARY'), r('StateLocalGovernment', '05', 'UTAH STATE GOVERNMENT'), r('Other', '06', 'HEBER VALLEY HISTORIC RAILROAD'), r('Other', '07', 'CONSTRUCTION MATERIAL PURCHASED FOR AIRPORTS'),
    ],
    reasonText: [], defaultReason: null, blanket: [], defaultBlanket: null,
  },
  {
    code: 'TC-721', stateCode: 'UT', signature: ['businessname', 'sales tax license number', '1'], cues: ['TC-721'],
    purchaserName: [f('businessname', 'Name of business or institution claiming exemption (purchaser)', 'below')], identifier: [f('sales tax license number', 'Sales Tax License Number:')], signedOn: [f('date01', 'Date', 'below')], expiresOn: [],
    reasons: [
      r('Resale', '1', 'Resale or Re-lease'), r('Manufacturing', '2', 'Fuels, Gas, Electricity'), r('Manufacturing', '3', 'Machinery and Equipment and Normal Operating'), r('Manufacturing', '4'), r('Other', '5'), r('Manufacturing', '6', 'Refinery Machinery, Equipment and'),
      r('Other', '7', 'Pollution Control Facility'), r('Other', '8', 'Municipal Energy'), r('Other', '9', 'Alternative Energy'), r('Other', '10'), r('Other', '11', 'Electrical Cooperatives'), r('Other', '12', 'Film and Television'), r('Other', '13'),
      r('Other', '14', 'Service Provider Consumables'), r('Other', '15', 'Commercial Airlines'), r('Other', '16'), r('Other', '17', 'Mailing Lists'), r('Manufacturing', '18'), r('Other', '19', 'Telecommunications Equipment,'), r('Other', '20'),
      r('Other', '21', 'Ski Resort'), r('Other', '22', 'Qualifying Data Center'), r('Other', '23', 'Locomotive Fuel'), r('Other', '24', 'Leasebacks'), r('Other', '25', 'Out-of-State Construction Materials'), r('Agricultural', '26', 'Agricultural Producer'),
      r('Other', '27', 'Tourism/Motor Vehicle Rental'), r('Educational', '28', 'Textbooks for Higher Education'), r('DirectMail', '30', 'Direct Mail'), r('Manufacturing', '31'), r('Other', '32', 'Sports Zone Construction Materials'),
    ],
    reasonText: [], defaultReason: null, blanket: [], defaultBlanket: null,
  },
  {
    code: 'SST', stateCode: null, signature: ['Item.NR.FederalGovernment', 'Item.Checkbox2'], cues: ['Streamlined Sales'],
    purchaserName: [f('Item.Your.Name', 'Name of purchaser', 'below')], identifier: SST_STATES.map((state) => f(`Item.${state}IdentificationNumber`)), signedOn: [f('Item.Date', 'Date', 'below')], expiresOn: [],
    reasons: [
      r('FederalGovernment', 'Item.Checkbox2', 'Federal government', 'Federal government'), r('StateLocalGovernment', 'Item.Checkbox2', 'State or local government', 'state or local gov'), r('TribalGovernment', 'Item.Checkbox2', 'Tribal government', 'tribal goverment'),
      r('ForeignDiplomat', 'Item.Checkbox2', 'Foreign diplomat', 'Foreign diplomat'), r('Charitable', 'Item.Checkbox2', 'Charitable organization', 'charitable organization'), r('Religious', 'Item.Checkbox2', 'Religious organization', 'religious or educational organization'),
      r('Resale', 'Item.Checkbox2', 'Resale', 'resale'), r('Agricultural', 'Item.Checkbox2', 'Agricultural Production', 'agricultural production'), r('Manufacturing', 'Item.Checkbox2', 'Industrial production/manufacturing', 'Industrial'),
      r('DirectPay', 'Item.Checkbox2', 'Direct pay permit', 'Directy pay permit'), r('DirectMail', 'Item.Checkbox2', 'Direct Mail', 'direct mail'), r('Other', 'Item.Checkbox2', 'Other', 'Other'), r('Educational', 'Item.Checkbox2', 'Educational Organization', 'Educational'),
    ],
    reasonText: [], defaultReason: null,
    blanket: [b(false, 'Item.Multistate', 'Check if this certificate is for a single purchase.')], defaultBlanket: true,
  },
  {
    code: 'MTC', stateCode: null, signature: ['Name of Firm Buyer'], cues: ['Uniform Sales & Use Tax'],
    purchaserName: [f('Name of Firm Buyer', 'Name of Firm (Buyer):')], identifier: MTC_STATES.map((state) => f(`State Registration Sellers Permit or ID Number of Purchaser${state}`)), signedOn: [f('Date', 'Date:')], expiresOn: [],
    reasons: [], reasonText: [], defaultReason: 'Resale', blanket: [], defaultBlanket: null,
  },
];

/**
 * The template whose signature fields all stand on the form, or null. Where
 * two forms share the field names (Utah's boxes are numbered like Ohio's
 * lines), the one whose printed cues the text carries wins, then the first
 * listed.
 */
export function templateForFields(fields: FormFields, text: string | null = null): CertificateFormTemplate | null {
  const names = Object.keys(fields);
  if (names.length === 0) return null;
  const matching = CERTIFICATE_FORM_TEMPLATES.filter((template) => template.signature.length > 0 && template.signature.every((name) => names.includes(name)));
  if (matching.length > 1 && text) {
    const lower = text.toLowerCase();
    const cued = matching.find((template) => template.cues.length > 0 && template.cues.every((cue) => lower.includes(cue.toLowerCase())));
    if (cued) return cued;
  }
  return matching[0] ?? null;
}

/** The template whose cues all appear in the text, the longest set of cues winning, or null. */
export function templateForText(text: string): CertificateFormTemplate | null {
  const lower = text.toLowerCase();
  let best: { template: CertificateFormTemplate; weight: number } | null = null;
  for (const template of CERTIFICATE_FORM_TEMPLATES) {
    if (template.cues.length === 0 || !template.cues.every((cue) => lower.includes(cue.toLowerCase()))) continue;
    const weight = template.cues.reduce((sum, cue) => sum + cue.length, 0);
    if (best === null || weight > best.weight) best = { template, weight };
  }
  return best?.template ?? null;
}

/** Every label a template prints, so a value read after one stops at the next. */
function labelsOf(template: CertificateFormTemplate): string[] {
  const anchors: FormAnchor[] = [...template.purchaserName, ...template.identifier, ...template.signedOn, ...template.expiresOn, ...template.reasons, ...template.reasonText, ...template.blanket];
  return anchors.map((a) => a.label).filter((label): label is string => !!label);
}

function fieldText(fields: FormFields, anchor: FormAnchor): string | null {
  if (!anchor.field) return null;
  const names = typeof anchor.field === 'string' ? [anchor.field] : anchor.field;
  const parts = names.map((name) => fields[name]).filter((v): v is string => typeof v === 'string').map((v) => v.trim()).filter(Boolean);
  if (parts.length === 0) return null;
  return parts.join(anchor.join ?? ' ');
}

/** Whether a box or a radio is ticked: its own value where the rule names one, else any ticked state or a non-empty text. */
function ticked(value: FormFieldValue | undefined, wanted: string | undefined): boolean {
  if (value === undefined) return false;
  if (wanted !== undefined) return value === wanted;
  if (typeof value === 'boolean') return value;
  return value.trim() !== '' && value !== 'Off';
}

function firstText(fields: FormFields, anchors: readonly FormAnchor[]): string | null {
  for (const anchor of anchors) {
    const text = fieldText(fields, anchor);
    if (text) return text;
  }
  return null;
}

/** The facts a filled form's fields carry, read through its template; a fact the form did not say stays null. */
export function readCertificateFields(template: CertificateFormTemplate, fields: FormFields): CertificateFacts {
  let reasonCode: string | null = null;
  for (const anchor of template.reasonText) {
    const text = fieldText(fields, anchor);
    if (text) reasonCode = detectExemptionReason(text) ?? 'Other';
    if (reasonCode) break;
  }
  if (!reasonCode) {
    for (const rule of template.reasons) {
      if (!rule.field || !ticked(fields[rule.field as string], rule.value)) continue;
      reasonCode = rule.fromText ? (detectExemptionReason(fieldText(fields, { field: rule.fromText }) ?? '') ?? rule.code) : rule.code;
      break;
    }
  }
  let isBlanket: boolean | null = null;
  for (const rule of template.blanket) {
    if (rule.field && ticked(fields[rule.field as string], rule.value)) {
      isBlanket = rule.blanket;
      break;
    }
  }
  return {
    formCode: template.code,
    stateCode: template.stateCode,
    purchaserName: firstText(fields, template.purchaserName),
    identifier: firstText(fields, template.identifier),
    isBlanket: isBlanket ?? template.defaultBlanket,
    reasonCode: reasonCode ?? template.defaultReason,
    signedOn: parseFormDate(firstText(fields, template.signedOn)),
    expiresOn: parseFormDate(firstText(fields, template.expiresOn)),
  };
}

/**
 * The facts a picture's words carry, read through the template's printed
 * labels: a value is what follows its label, and a reason or a blanket box
 * is ticked by the ink beside its label, or by the mark read there.
 */
export function readCertificateWords(template: CertificateFormTemplate, page: OcrPage, pixels: PixelSampler | null): CertificateFacts {
  const lines = linesOf(page);
  const others = labelsOf(template);
  const value = (anchors: readonly FormAnchor[]): string | null => {
    for (const anchor of anchors) {
      if (!anchor.label) continue;
      const found = findLabel(lines, anchor.label);
      if (!found) continue;
      const text = valueAtLabel(lines, found, anchor.where ?? 'right', others.filter((o) => o !== anchor.label));
      if (text) return text;
    }
    return null;
  };
  let reasonCode: string | null = null;
  for (const anchor of template.reasonText) {
    const text = value([anchor]);
    if (text) reasonCode = detectExemptionReason(text) ?? 'Other';
    if (reasonCode) break;
  }
  if (!reasonCode) {
    for (const rule of template.reasons) {
      if (!rule.label) continue;
      const found = findLabel(lines, rule.label);
      if (!found || !tickedBesideLabel(found, pixels)) continue;
      reasonCode = rule.fromText ? (detectExemptionReason(valueAtLabel(lines, found, 'right', others.filter((o) => o !== rule.label))) ?? rule.code) : rule.code;
      break;
    }
  }
  let isBlanket: boolean | null = null;
  for (const rule of template.blanket) {
    if (!rule.label) continue;
    const found = findLabel(lines, rule.label);
    if (found && tickedBesideLabel(found, pixels)) {
      isBlanket = rule.blanket;
      break;
    }
  }
  return {
    formCode: template.code,
    stateCode: template.stateCode,
    purchaserName: value(template.purchaserName),
    identifier: value(template.identifier),
    isBlanket: isBlanket ?? template.defaultBlanket,
    reasonCode: reasonCode ?? template.defaultReason,
    signedOn: parseFormDate(value(template.signedOn)),
    expiresOn: parseFormDate(value(template.expiresOn)),
  };
}

/** The facts a plain text carries with no template: the state and the reason its words name, and nothing else. */
export function readCertificateText(text: string): CertificateFacts {
  return { ...EMPTY_CERTIFICATE_FACTS, formCode: detectFormCode(text) ?? (isMultistateForm(text) ? 'SST' : null), stateCode: detectStateCode(text), reasonCode: detectExemptionReason(text) };
}
